import React, {useCallback, useEffect, useState} from 'react';
import Layout from '../components/Layout';
import {api} from '../../api';

export default function BankFeeds() {
  const [connections, setConnections] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [linking, setLinking] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadConnections = () => api('/plaid/connections').then(r => r.ok ? r.json() : []).then(setConnections);
  const loadAccounts = useCallback((connId) => {
    const url = connId ? `/plaid/accounts?connectionId=${connId}` : '/plaid/accounts';
    api(url).then(r => r.ok ? r.json() : []).then(setAccounts);
  }, []);
  const loadTransactions = useCallback((connId, acctId) => {
    const params = new URLSearchParams();
    if (connId) params.set('connectionId', connId);
    if (acctId) params.set('accountId', acctId);
    const qs = params.toString();
    api(`/plaid/transactions${qs ? '?' + qs : ''}`).then(r => r.ok ? r.json() : []).then(setTransactions);
  }, []);

  useEffect(() => { loadConnections(); loadAccounts(); loadTransactions(); }, [loadAccounts, loadTransactions]);

  const handleConnect = async () => {
    setLinking(true);
    try {
      const res = await api('/plaid/link-token', {method: 'POST'});
      if (!res.ok) { setLinking(false); return; }
      const {linkToken} = await res.json();

      const handler = window.Plaid.create({
        token: linkToken,
        onSuccess: async (publicToken, metadata) => {
          await api('/plaid/exchange-token', {
            method: 'POST',
            body: JSON.stringify({
              publicToken,
              institutionId: metadata.institution?.institution_id || '',
              institutionName: metadata.institution?.name || '',
            }),
          });
          loadConnections();
          loadAccounts(selectedConnection || undefined);
          loadTransactions(selectedConnection || undefined, selectedAccount || undefined);
          setLinking(false);
        },
        onExit: () => setLinking(false),
      });
      handler.open();
    } catch { setLinking(false); }
  };

  const handleSync = async (connId) => {
    setSyncing(connId);
    await api(`/plaid/connections/${connId}`, {method: 'POST'});
    loadTransactions(selectedConnection || undefined, selectedAccount || undefined);
    loadConnections();
    setSyncing(null);
  };

  const handleDelete = async (connId) => {
    await api(`/plaid/connections/${connId}`, {method: 'DELETE'});
    loadConnections();
    if (selectedConnection === connId) {
      setSelectedConnection('');
      setSelectedAccount('');
      loadAccounts();
      loadTransactions();
    } else {
      loadAccounts(selectedConnection || undefined);
      loadTransactions(selectedConnection || undefined, selectedAccount || undefined);
    }
  };

  const handleConnectionFilter = (connId) => {
    setSelectedConnection(connId);
    setSelectedAccount('');
    loadAccounts(connId || undefined);
    loadTransactions(connId || undefined);
  };

  const handleAccountFilter = (acctId) => {
    setSelectedAccount(acctId);
    loadTransactions(selectedConnection || undefined, acctId || undefined);
  };

  const filtered = transactions.filter(t =>
    `${t.name || ''} ${t.merchantName || ''} ${t.category || ''} ${t.accountName || ''}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      {/* Connections */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <h2 className="text-xl font-semibold text-gray-900">Bank Connections</h2>
          <button onClick={handleConnect} disabled={linking}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            {linking ? 'Connecting...' : '+ Connect Bank'}
          </button>
        </div>
        {connections.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No bank accounts connected. Click "Connect Bank" to get started.</div>
        ) : (
          <div className="divide-y">
            {connections.map(c => (
              <div key={c.id} className="p-4 md:px-6 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{c.institutionName || 'Bank Account'}</p>
                  <p className="text-sm text-gray-500">
                    {c.lastSynced ? `Last synced ${new Date(c.lastSynced).toLocaleString()}` : 'Not yet synced'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleSync(c.id)} disabled={syncing === c.id}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                    {syncing === c.id ? 'Syncing...' : 'Sync Now'}
                  </button>
                  <button onClick={() => handleDelete(c.id)}
                    className="px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50">
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Transactions */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 md:p-6 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">Transactions</h2>
            {connections.length > 1 && (
              <select value={selectedConnection} onChange={e => handleConnectionFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none text-sm">
                <option value="">All Connections</option>
                {connections.map(c => <option key={c.id} value={c.id}>{c.institutionName}</option>)}
              </select>
            )}
            {accounts.length > 1 && (
              <select value={selectedAccount} onChange={e => handleAccountFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none text-sm">
                <option value="">All Accounts</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}{a.mask ? ` ••${a.mask}` : ''}</option>)}
              </select>
            )}
          </div>
          <input type="text" placeholder="Search transactions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-full mt-3 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map(t => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600 text-sm">{t.date}</td>
                  <td className="px-6 py-4 text-gray-900">
                    <p className="font-medium">{t.merchantName || t.name}</p>
                    {t.merchantName && t.name !== t.merchantName && <p className="text-sm text-gray-500">{t.name}</p>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600 text-sm">{t.category || '—'}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-right font-medium ${t.amount < 0 ? 'text-green-600' : 'text-gray-900'}`}>
                    {t.amount < 0 ? '+' : '-'}${Math.abs(t.amount).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {t.pending
                      ? <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending</span>
                      : <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Posted</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="text-center py-12 text-gray-500">No transactions found.</div>}
        </div>
      </div>
    </Layout>
  );
}
